// src/hooks/useElettrodotti.ts
// DOS518 – Hook React: fetch, cache e stato layer elettrodotti

import { useState, useEffect, useCallback, useRef } from 'react';
import * as L from 'leaflet';
import {
  fetchElettrodottiOverpass,
  ElettrodottiGeoJSON,
  ElettrodottoFeature,
} from '../utils/elettrodottiOverpass';
import {
  getCachedElettrodotti,
  setCachedElettrodotti,
} from '../utils/elettrodottiCache';

// ─── Tipi ─────────────────────────────────────────────────────────────────────

export type StatoFetch =
  | 'idle'      // layer disattivato
  | 'loading'   // fetch in corso
  | 'success'   // dati disponibili
  | 'cached'    // dati da cache offline
  | 'offline'   // offline + no cache
  | 'error';    // errore di rete

export interface UseElettrodottiReturn {
  stato: StatoFetch;
  data: ElettrodottiGeoJSON | null;
  errore: string | null;
  /** Features che intersecano un bounds specifico (per briefing). */
  featuresInBounds: (bounds: L.LatLngBounds) => ElettrodottoFeature[];
  /** Forza un re-fetch ignorando la cache. */
  ricarica: () => void;
  /** True se ci sono elettrodotti AAT/AT nella bbox corrente della mappa. */
  pericoloAereo: boolean;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * @param map     Istanza Leaflet Map (null durante init)
 * @param enabled True quando il layer è attivato dall'utente
 */
export function useElettrodotti(
  map: L.Map | null,
  enabled: boolean
): UseElettrodottiReturn {
  const [stato, setStato]   = useState<StatoFetch>('idle');
  const [data, setData]     = useState<ElettrodottiGeoJSON | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const abortRef            = useRef<AbortController | null>(null);
  const forceRef            = useRef(false);

  // ── Logica principale di caricamento ────────────────────────────────────────

  const carica = useCallback(
    async (force = false) => {
      if (!map || !enabled) return;

      const bounds = map.getBounds();

      // 1. Controlla cache (a meno di forzatura)
      if (!force) {
        const cached = await getCachedElettrodotti(bounds);
        if (cached) {
          setData(cached);
          setStato('cached');
          setErrore(null);
          return;
        }
      }

      // 2. Offline senza cache
      if (!navigator.onLine) {
        setStato('offline');
        setErrore('Dati non disponibili offline. Attivare connessione e ricaricare.');
        return;
      }

      // 3. Fetch remoto
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      setStato('loading');
      setErrore(null);

      try {
        const geojson = await fetchElettrodottiOverpass(bounds, abortRef.current.signal);
        await setCachedElettrodotti(bounds, geojson);
        setData(geojson);
        setStato('success');
      } catch (err: any) {
        if (err.name === 'AbortError') return; // intentional
        setStato('error');
        setErrore(err.message || 'Errore fetch elettrodotti');
      }
    },
    [map, enabled]
  );

  // ── Attivazione/disattivazione layer ────────────────────────────────────────

  useEffect(() => {
    if (!enabled) {
      abortRef.current?.abort();
      setData(null);
      setStato('idle');
      setErrore(null);
      return;
    }
    carica(forceRef.current);
    forceRef.current = false;
  }, [enabled, carica]);

  // ── Aggiorna al moveend (debounced 800 ms) ───────────────────────────────────

  useEffect(() => {
    if (!map || !enabled) return;

    let timer: ReturnType<typeof setTimeout>;
    const onMoveEnd = () => {
      clearTimeout(timer);
      timer = setTimeout(() => carica(), 800);
    };

    map.on('moveend', onMoveEnd);
    return () => {
      map.off('moveend', onMoveEnd);
      clearTimeout(timer);
    };
  }, [map, enabled, carica]);

  // ── Funzione: features che intersecano un bounds ──────────────────────────────

  const featuresInBounds = useCallback(
    (targetBounds: L.LatLngBounds): ElettrodottoFeature[] => {
      if (!data) return [];
      return data.features.filter(f =>
        f.geometry.coordinates.some(([lon, lat]) =>
          targetBounds.contains(L.latLng(lat, lon))
        )
      );
    },
    [data]
  );

  // ── Flag pericolo aereo ───────────────────────────────────────────────────────

  const pericoloAereo =
    !!data && data.features.some(f =>
      f.properties.classe === 'AAT' || f.properties.classe === 'AT'
    );

  // ── API pubblica ──────────────────────────────────────────────────────────────

  const ricarica = useCallback(() => {
    forceRef.current = true;
    carica(true);
  }, [carica]);

  return { stato, data, errore, featuresInBounds, ricarica, pericoloAereo };
}
